import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { makeStyles, Box, Grid, Typography } from "@material-ui/core";

import {
  ComposedChart,
  Area,
  Bar,
  XAxis,
  YAxis,
  ReferenceLine,
  ResponsiveContainer,
  LabelList,
} from "recharts";

import { COLORS } from "../../../../../theme/colors";
import ArrowLeftImg from "../../../../../assets/images/icons/arrow-left.svg";
import ArrowRightImg from "../../../../../assets/images/icons/arrow-right.svg";
import { ShaDowBox } from "../../../../../components/boxes";
import { Icon, ButtonWithTooltip } from "../../../../../components/base";
import { GraphFooter, NoDataCard } from "../../../../../components/parts";
import {
  unrealizedTradingRevenuesBars,
  unrealizedTradingRevenuesGraphItems,
} from "../../../constants";
import {
  getData,
  currencyFormatter,
  onlyZeroValuesInArray,
  extractValuesFromArrayOfObjects,
} from "../../../../../services";

const useStyles = makeStyles(() => ({
  arrow: {
    zIndex: 1,
    position: "absolute",
    top: 10,
  },
  text: {
    position: "absolute",
    top: -20,
    fontSize: 48,
    fontWeight: 600,
    left: 66,
  },
}));

const topLabel = (props) => {
  const { x, y, value, name, height, width, color } = props;

  if (!value) return null;

  return (
    <g>
      <text
        x={x}
        y={y}
        dy={height - 24}
        dx={width / 2}
        fill={color}
        fontSize={14}
        textAnchor="middle"
      >
        <tspan fill={color}>{value ? name : ""}</tspan>
        <tspan x={x} y={y} dy={height - 10} dx={width / 2} fill={color}>
          Avg.
        </tspan>
      </text>
      <svg x={x - 30 + width / 2} y={36} fill={color}>
        <path d="M 0 0 L 0 4 L 60 4 L 60 0 L 0 0" />
      </svg>
    </g>
  );
};

const bottomLabel = (props) => {
  const { user } = useSelector((state) => state.auth);
  const currencySymbol = user.currency.symbol;
  const { x, y, height, value, width } = props;

  if (!value) return null;

  return (
    <g>
      <text
        x={x}
        y={y}
        dy={0 - height + 20}
        dx={width / 2}
        fill={COLORS.red}
        textAnchor="middle"
        style={{ fontSize: 14, fontWeight: 700 }}
      >
        {currencyFormatter(value, currencySymbol)}
        <tspan style={{ fontWeight: 400 }}>/{user?.unit_system}</tspan>
      </text>
    </g>
  );
};

export const AvgUnrealizedPraasTradingRevenues = ({ unit }) => {
  const classes = useStyles();
  const [step, setStep] = useState(0);
  const [chartData, setChartData] = useState([]);
  const title = "Avg. Unrealized PRaaS Trading Revenues";

  const calculateTotal = (obj) => {
    return obj.unconnected_tenants + obj.economic_vacancy + obj.physical_vacancy;
  };

  useEffect(() => {
    getData(
      `/praas/v2/buildings/${unit.building.id}/properties/units/${unit.id}/unrealized-trading/`,
      (results) => {
        const data = results.map((result) => {
          const { trunc_date, unit, building, portfolio, industry } = result;

          const year = new Date(trunc_date).getUTCFullYear();
          if (
            onlyZeroValuesInArray(
              extractValuesFromArrayOfObjects([unit, building, portfolio, industry])
            )
          ) {
            return {
              year,
              results: [],
            };
          }

          const unitTotal = calculateTotal(unit);
          const buildingTotal = calculateTotal(building);
          const portfolioTotal = calculateTotal(portfolio);
          const industryTotal = calculateTotal(industry);
          const max = Math.max(unitTotal, buildingTotal, portfolioTotal, industryTotal);
          const negative = max * 3;

          return {
            year,
            results: [
              {
                name: "A",
                negative: -negative,
              },
              {
                name: "B",
                negative: -negative,
              },
              {
                name: "C",
                negative: -negative,
              },
              {
                name: "Unit",
                suffix: "Avg.",
                unconnected_tenants: -unit.unconnected_tenants,
                economic_vacancy: -unit.economic_vacancy,
                physical_vacancy: -unit.physical_vacancy,
                negative: -negative,
                total: unitTotal,
                color: COLORS.red,
              },
              {
                name: "Building",
                suffix: "Avg.",
                unconnected_tenants: -building.unconnected_tenants,
                economic_vacancy: -building.economic_vacancy,
                physical_vacancy: -building.physical_vacancy,
                negative: -negative,
                total: buildingTotal,
                color: COLORS.blue,
              },
              {
                name: "Portfolio",
                suffix: "Avg.",
                unconnected_tenants: -portfolio.unconnected_tenants,
                economic_vacancy: -portfolio.economic_vacancy,
                physical_vacancy: -portfolio.physical_vacancy,
                negative: -negative,
                total: portfolioTotal,
                color: COLORS.orange,
              },
              {
                name: "Industry",
                suffix: "Avg.",
                unconnected_tenants: -industry.unconnected_tenants,
                economic_vacancy: -industry.economic_vacancy,
                physical_vacancy: -industry.physical_vacancy,
                negative: -negative,
                total: industryTotal,
                color: COLORS.darks[0][100],
              },
              {
                name: "D",
                negative: -negative,
              },
              {
                name: "E",
                negative: -negative,
              },
              {
                name: "F",
                negative: -negative,
              },
            ],
          };
        });
        setChartData(data);
      }
    );

    return () => setChartData([]);
  }, []);

  const previousStep = () => {
    if (step === 0) return;
    setStep((prev) => prev - 1);
  };

  const nextStep = () => {
    if (step === chartData.length - 1) return;
    setStep((prev) => prev + 1);
  };

  if (!chartData.length)
    return (
      <NoDataCard
        title={title}
        footerNames={["Physical vacancy", "Economic vacancy", "Unconnected tenants"]}
      />
    );

  return (
    <ShaDowBox fullHeight>
      <Box style={{ padding: 24 }}>
        <Grid container justifyContent="space-between" style={{ marginBottom: 40 }}>
          <Typography variant="h4">{title}</Typography>
          <ButtonWithTooltip disabled text="Optimize Tenants Profitability" title="Comming soon" />
        </Grid>
        <Box style={{ marginTop: 24, height: 250, position: "relative" }}>
          {chartData.length ? (
            <>
              <Icon
                root={classes.arrow}
                hoverable={true}
                src={ArrowLeftImg}
                style={{
                  left: 34,
                }}
                onClick={previousStep}
              />
              <Icon
                root={classes.arrow}
                hoverable={true}
                src={ArrowRightImg}
                style={{
                  right: 34,
                }}
                onClick={nextStep}
              />
              <Typography className={classes.text}>{chartData[step].year}</Typography>
              {chartData[step].results.length ? (
                <ResponsiveContainer>
                  <ComposedChart
                    width="100%"
                    height="100%"
                    data={chartData[step].results}
                    stackOffset="sign"
                    margin={{
                      top: 40,
                    }}
                  >
                    <XAxis dataKey="name" scale="point" hide={true} />
                    <YAxis hide={true} />
                    <ReferenceLine y={0} stroke="#000" />
                    <Area
                      type="monotone"
                      dataKey="negative"
                      fill={COLORS.deepPeach[0][2]}
                      stroke={COLORS.deepPeach[0][2]}
                    />
                    {unrealizedTradingRevenuesBars.map((bar, i) => (
                      <Bar
                        key={i}
                        dataKey={bar.dataKey}
                        barSize={30}
                        fill={bar.fill}
                        stackId="stack"
                        stroke="#000"
                        strokeDasharray="3 3"
                      >
                        {bar.dataKey === "unconnected_tenants" && (
                          <LabelList dataKey="total" content={(props) => topLabel(props)} />
                        )}
                        {bar.dataKey === "economic_vacancy" && (
                          <LabelList dataKey="total" content={(props) => bottomLabel(props)} />
                        )}
                      </Bar>
                    ))}
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <NoDataCard
                  withShaDowBox={false}
                  footerNames={["Physical vacancy", "Economic vacancy", "Unconnected tenants"]}
                  boxStyle={{ padding: 0 }}
                  gridStyle={{ height: 170 }}
                />
              )}
            </>
          ) : null}
          {chartData[step].results.length ? (
            <GraphFooter
              items={unrealizedTradingRevenuesGraphItems}
              style={{ position: "absolute", bottom: 10 }}
              typographyStyle={{ fontSize: 12, fontWeight: 700 }}
            />
          ) : null}
        </Box>
      </Box>
    </ShaDowBox>
  );
};
